import { type ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { resolveSemanticColor } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { TableCellContainer } from '@/ui/composites/table';
import { OverflowTooltip } from '@/ui/composites/table/cells/OverflowTooltip';
import { Box, IconButton, Link, Text, useTheme } from '@/ui/primitives';
import {
  KeyboardArrowDownOutlinedIcon,
  KeyboardArrowRightOutlinedIcon,
} from '@/ui/primitives/icons';
import type { EntityDailyHistoryRow } from './EntityDailyHistoryPanel.types';
import type { SemanticColorKey } from '@/theme/semanticTokens';
import type { SxProps, Theme } from '@/ui/primitives/types';

export type NonArraySx = Exclude<SxProps<Theme>, readonly unknown[]>;

export const CHILD_NAME_INDENT = s('xl');
export const EXPAND_ICON_GUTTER_WIDTH = '24px';
const EXPAND_ICON_SIZE = '18px';

const HISTORY_NAME_CONTENT_SX = {
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  width: '100%',
  maxWidth: '100%',
  flex: 1,
  overflow: 'hidden',
} as const;

const buildHistoryNameTitle = (row: EntityDailyHistoryRow): string => row.name;

const renderHistoryPrefixParts = (
  row: EntityDailyHistoryRow,
  rowNameTextSx: NonArraySx,
  linksDisabled: boolean,
): ReactElement | null => {
  const parts = row.prefixParts ?? [];
  if (parts.length === 0) {
    return null;
  }

  return (
    <>
      {parts.map((part, index) => (
        <Box
          key={`${row.id}-prefix-part-${String(index)}`}
          sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
        >
          {part.href && !linksDisabled ? (
            <Link
              component={RouterLink}
              to={part.href}
              underline="hover"
              color="inherit"
              sx={[
                rowNameTextSx,
                {
                  color: 'text.secondary',
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                },
              ]}
            >
              {part.label}
            </Link>
          ) : (
            <Text
              role="body"
              sx={[
                rowNameTextSx,
                {
                  color: 'text.secondary',
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                },
              ]}
            >
              {part.label}
            </Text>
          )}
          <Text
            role="body"
            sx={[
              rowNameTextSx,
              {
                color: 'text.secondary',
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                mx: s('sm'),
              },
            ]}
          >
            /
          </Text>
        </Box>
      ))}
    </>
  );
};

export { HistoryStatusesGrid } from './EntityDailyHistoryStatusCells';

export const HistoryNameCell = ({
  row,
  rowNameTextSx,
  childRowIndent,
  nameColor = 'inherit',
  showExpand,
  isExpanded,
  onToggle,
  linksDisabled,
}: {
  row: EntityDailyHistoryRow;
  rowNameTextSx: NonArraySx;
  childRowIndent?: string;
  nameColor?: SemanticColorKey | 'inherit';
  showExpand?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  linksDisabled: boolean;
}): ReactElement => {
  const fullNameTitle = buildHistoryNameTitle(row);
  const theme = useTheme();
  const resolvedNameColor =
    nameColor === 'inherit' ? nameColor : resolveSemanticColor(theme, nameColor);

  return (
    <TableCellContainer
      gap="none"
      paddingTop="none"
      sx={{
        minHeight: '32px',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          width: '100%',
          pl: childRowIndent ?? '0',
        }}
      >
        <Box sx={HISTORY_NAME_CONTENT_SX}>
          {row.prefixParts && row.prefixParts.length > 0 ? (
            renderHistoryPrefixParts(row, rowNameTextSx, linksDisabled)
          ) : row.prefixLabel ? (
            row.prefixHref && !linksDisabled ? (
              <Link
                component={RouterLink}
                to={row.prefixHref}
                underline="hover"
                color="inherit"
                sx={[
                  rowNameTextSx,
                  {
                    color: 'text.secondary',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  },
                ]}
              >
                {row.prefixLabel}
              </Link>
            ) : (
              <Text
                role="body"
                sx={[
                  rowNameTextSx,
                  {
                    color: 'text.secondary',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  },
                ]}
              >
                {row.prefixLabel}
              </Text>
            )
          ) : null}
          {!(row.prefixParts && row.prefixParts.length > 0) && row.prefixLabel ? (
            <Text
              role="body"
              sx={[
                rowNameTextSx,
                {
                  color: 'text.secondary',
                  display: 'inline-flex',
                  alignItems: 'center',
                  flexShrink: 0,
                  mx: s('sm'),
                },
              ]}
            >
              /
            </Text>
          ) : null}
          <OverflowTooltip
            title={fullNameTitle}
            wrapperSx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            {linksDisabled ? (
              <Text
                role="body"
                sx={[
                  rowNameTextSx,
                  {
                    color: resolvedNameColor,
                    display: 'block',
                    width: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                ]}
              >
                {row.name}
              </Text>
            ) : (
              <Link
                component={RouterLink}
                to={row.href}
                underline="hover"
                color="inherit"
                sx={[
                  rowNameTextSx,
                  {
                    color: resolvedNameColor,
                    display: 'inline-block',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    verticalAlign: 'top',
                  },
                ]}
              >
                {row.name}
              </Link>
            )}
          </OverflowTooltip>
        </Box>
        {showExpand && onToggle ? (
          <IconButton
            aria-label={isExpanded ? `Collapse ${row.name}` : `Expand ${row.name}`}
            onClick={onToggle}
            sx={{
              color: resolvedNameColor,
              ml: s('xs'),
              flexShrink: 0,
              width: EXPAND_ICON_GUTTER_WIDTH,
              height: EXPAND_ICON_GUTTER_WIDTH,
              p: s('none'),
              opacity: 0.82,
              '&:hover': {
                backgroundColor: 'transparent',
                color: resolvedNameColor,
                opacity: 1,
              },
              '&.Mui-focusVisible, &:focus-visible': {
                outline: (muiTheme) => `2px solid ${muiTheme.app.state.focus}`,
                outlineOffset: 1,
              },
            }}
          >
            {isExpanded ? (
              <KeyboardArrowDownOutlinedIcon
                sx={{
                  fontSize: EXPAND_ICON_SIZE,
                  width: EXPAND_ICON_SIZE,
                  height: EXPAND_ICON_SIZE,
                }}
              />
            ) : (
              <KeyboardArrowRightOutlinedIcon
                sx={{
                  fontSize: EXPAND_ICON_SIZE,
                  width: EXPAND_ICON_SIZE,
                  height: EXPAND_ICON_SIZE,
                }}
              />
            )}
          </IconButton>
        ) : showExpand ? (
          <Box sx={{ width: EXPAND_ICON_GUTTER_WIDTH, ml: s('xs'), flexShrink: 0 }} />
        ) : null}
      </Box>
    </TableCellContainer>
  );
};
